import { Credit } from "@/interfaces/credit";
import axios from "@/lib/axios";
import { useCreditStore } from "@/stores/credit";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { get } from "@vueuse/core";

import { MutationParams } from "@/interfaces/mutationParams";
import { storeToRefs } from "pinia";
import { MaybeRef, watch } from "vue";
import { useToast } from "vue-toastification";
import useCustomMutation from "./useCustomMutation";

interface Params extends Omit<Partial<Credit>, "id"> {
  creditId: MaybeRef<number | string | undefined>;
}

interface Props {
  params: Params;
  immediate?: boolean;
}

interface UpdateParams extends Params {}

interface DeleteParams {
  id: number | string;
}

interface RestoreParams {
  id: number | string;
}

const getCredit = async (params?: Params) => {
  if (!params?.creditId) return;
  const { data } = await axios.get(
    `/api/internal/credits/${get(params.creditId)}`,
  );
  return data;
};

const updateCredit = async (params: UpdateParams) => {
  const { data } = await axios.put(
    `/api/internal/credits/${params.creditId}`,
    params,
  );
  return data;
};

const deleteCompanyCredit = async (params: DeleteParams) => {
  const { data } = await axios.delete(`/api/internal/credits/${params.id}`);
  return data;
};

const restoreCompanyCredit = async (params: Pick<RestoreParams, "id">) => {
  const { data } = await axios.post(
    `/api/internal/credits/${params.id}/restore`,
  );
  return data;
};

const useCredit = (props?: Props) => {
  const toast = useToast();
  const { params, immediate = false } = props || {};

  const { setCredit } = useCreditStore();
  const { credit } = storeToRefs(useCreditStore());

  const { data, isLoading, refetch, isFetching, isError } = useQuery(
    ["credit"],
    () => getCredit({ ...params! }),
    {
      enabled: params ? immediate : false,
      retry: false,
    },
  );

  const updateMutation = useMutation(
    ({ params }: MutationParams<UpdateParams>) => updateCredit(params),
    useCustomMutation(toast, "update"),
  );

  const deleteMutation = useMutation(
    ({ params }: MutationParams<DeleteParams>) => deleteCompanyCredit(params),
    useCustomMutation(toast, "delete"),
  );

  const restoreMutation = useMutation(
    ({ params }: MutationParams<RestoreParams>) => restoreCompanyCredit(params),
    useCustomMutation(toast, "update"),
  );

  watch(data, () => {
    if (data.value) {
      setCredit(data.value);
    }
  });

  return {
    credit,
    isLoading,
    refetch,
    isFetching,
    isError,

    updateCompanyCreditMutation: updateMutation.mutate,
    updateCompanyCredit: updateMutation.mutate,
    updateCompanyCreditAsync: updateMutation.mutateAsync,
    updateCompanyCreditLoading: updateMutation.isLoading,

    deleteCompanyCreditMutation: deleteMutation.mutate,
    deleteCompanyCredit: deleteMutation.mutate,
    deleteCompanyCreditAsync: deleteMutation.mutateAsync,
    deleteCompanyCreditLoading: deleteMutation.isLoading,

    restoreCompanyCreditMutation: restoreMutation.mutate,
    restoreCompanyCredit: restoreMutation.mutate,
    restoreCompanyCreditAsync: restoreMutation.mutateAsync,
    restoreCompanyCreditLoading: restoreMutation.isLoading,
  };
};

export default useCredit;
